import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';
import InfoIcon from '@mui/icons-material/Info';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import UndoIcon from '@mui/icons-material/Undo';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TimelineIcon from '@mui/icons-material/Timeline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import FlagIcon from '@mui/icons-material/Flag';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';

import Checkbox from '@mui/material/Checkbox';
import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import SendReminderModal from '../components/SendReminderModal';
import EditableAmountCell from '../components/EditableAmountCell';

interface InvoiceComment {
  id: number;
  body: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    full_name: string;
    email: string;
  };
}

interface DailySummary {
  date: string;
  servman_resolved: number;
  docuware_resolved: number;
  ignored_count: number;
  total: number;
}

interface VendorDetails {
  vendor_id: string;
  vendor_name: string;
  location: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
}

interface ServmanInvoice {
  location: string;
  vendor_id: string;
  vendor_name: string;
  invoice_number: string;
  invoice_date: string;
  invoice_amount: number;
  trantype: string;
  synced_at: string;
}

interface DocuwareInvoice {
  location: string;
  vendor_id: string;
  invoice_number: string;
  invoice_date: string;
  invoice_amount: number;
  docid: string;
  dw_created_date: string;
  dw_modified_date: string;
  synced_at: string;
}

interface VendorInvoice {
  id: number;
  resolved_vendor_name: string;
  statement_number: string;
  invoice_date: string;
  notes?: string;
  line_items?: any[];
  raw_extracted_text?: string;
  textract_response?: any;
  pdf_url?: string;
  pdf_filename?: string;
  statement_content_type?: string;
  validation_status?: string;
  servman_matched?: boolean;
  docuware_matched?: boolean;
  validated_at?: string;
  location?: string;
  vendor_id?: string;
  vendor_email?: string;
  extraction_error?: string;
  vendor_details?: VendorDetails;
  source_type?: string;
  source_id?: number;
  received_at?: string;
  flagged_for_review?: boolean;
  vendor_manually_set?: boolean;
  created_at: string;
  updated_at: string;
}

const VendorInvoiceDetailPage: React.FunctionComponent = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const [invoice, setInvoice] = useState<VendorInvoice | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [editing, setEditing] = useState<boolean>(false);
  const [extractedTextModalOpen, setExtractedTextModalOpen] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'text' | 'json'>('text');
  const [revalidating, setRevalidating] = useState<boolean>(false);
  const [activeCategories, setActiveCategories] = useState<string[]>([
    'missing',
    'amount_discrepancy',
    'vendor_discrepancy',
    'ignored',
  ]);
  const [matchDetailModal, setMatchDetailModal] = useState<{
    open: boolean;
    type: 'servman' | 'docuware' | null;
    data: any;
  }>({
    open: false,
    type: null,
    data: null,
  });
  const [vendors, setVendors] = useState<VendorDetails[]>([]);
  const [loadingVendors, setLoadingVendors] = useState<boolean>(false);
  const [selectedVendor, setSelectedVendor] = useState<VendorDetails | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    vendor_id: '',
    location: '',
    notes: '',
  });
  const [comments, setComments] = useState<InvoiceComment[]>([]);
  const [newNote, setNewNote] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [activityLog, setActivityLog] = useState<DailySummary[]>([]);
  const [ignoreDialog, setIgnoreDialog] = useState<{ lineItemId: number | null; reason: string; submitting: boolean }>({
    lineItemId: null,
    reason: '',
    submitting: false,
  });
  const [selectedLineItemIds, setSelectedLineItemIds] = useState<number[]>([]);
  const [sendReminderOpen, setSendReminderOpen] = useState<boolean>(false);

  const toggleLineItemSelection = (lineItemId: number) => {
    setSelectedLineItemIds((prev) =>
      prev.includes(lineItemId) ? prev.filter((existingId) => existingId !== lineItemId) : [...prev, lineItemId],
    );
  };

  const fetchVendors = async (searchTerm = '') => {
    setLoadingVendors(true);
    try {
      const response: any = await axios.get('/vendors/servman_vendors', {
        params: { search: searchTerm },
      });
      setVendors(response.data || []);
    } catch (error: any) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/vendors/vendor_invoices/${id}`);
      const invoiceData = response.data;
      setInvoice(invoiceData);
      setFormData({
        vendor_id: invoiceData.vendor_id || '',
        location: invoiceData.location || '',
        notes: invoiceData.notes || '',
      });
      if (invoiceData.vendor_details) {
        setSelectedVendor(invoiceData.vendor_details);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch invoice details';
      showErrorSnackBar(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVendorChange = (_event: any, newValue: VendorDetails | null) => {
    setSelectedVendor(newValue);
    if (newValue) {
      setFormData({
        ...formData,
        vendor_id: newValue.vendor_id,
        location: newValue.location,
      });
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`/vendors/vendor_invoices/${id}`, {
        vendor_invoice: formData,
      });
      showSuccessSnackBar('Invoice updated successfully');
      setEditing(false);
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to update invoice';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      await axios.delete(`/vendors/vendor_invoices/${id}`);
      showSuccessSnackBar('Invoice deleted successfully');
      history.push('/vendors/invoices');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to delete invoice';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleMarkAsIgnored = async () => {
    try {
      await axios.post(`/vendors/vendor_invoices/${id}/ignore`);
      showSuccessSnackBar('Invoice marked as ignored');
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to mark invoice as ignored';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleRevalidate = async () => {
    setRevalidating(true);
    try {
      await axios.post(`/vendors/vendor_invoices/${id}/revalidate`);
      showSuccessSnackBar('Invoice re-validated successfully');
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Failed to re-validate invoice';
      showErrorSnackBar(errorMessage);
    } finally {
      setRevalidating(false);
    }
  };

  const handleToggleFlag = async () => {
    try {
      const response: any = await axios.post(`/vendors/vendor_invoices/${id}/toggle_flag`);
      showSuccessSnackBar(response.data?.message || 'Flag updated');
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Failed to toggle flag';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleUnignoreLineItem = async (lineItemId: number) => {
    try {
      await axios.post(`/vendors/vendor_invoices/${id}/line_items/${lineItemId}/unignore`);
      showSuccessSnackBar('Line item unignored');
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to update line item';
      showErrorSnackBar(errorMessage);
    }
  };

  const openIgnoreDialog = (lineItemId: number) => {
    setIgnoreDialog({ lineItemId, reason: '', submitting: false });
  };

  const closeIgnoreDialog = () => {
    if (ignoreDialog.submitting) return;
    setIgnoreDialog({ lineItemId: null, reason: '', submitting: false });
  };

  const submitIgnoreDialog = async () => {
    const reason = ignoreDialog.reason.trim();
    if (!reason) {
      showErrorSnackBar('Please enter a reason for ignoring this line');
      return;
    }
    if (ignoreDialog.lineItemId == null) return;

    setIgnoreDialog((prev) => ({ ...prev, submitting: true }));
    try {
      await axios.post(`/vendors/vendor_invoices/${id}/line_items/${ignoreDialog.lineItemId}/ignore`, {
        ignore_reason: reason,
      });
      showSuccessSnackBar('Line item ignored');
      setIgnoreDialog({ lineItemId: null, reason: '', submitting: false });
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to ignore line item';
      showErrorSnackBar(errorMessage);
      setIgnoreDialog((prev) => ({ ...prev, submitting: false }));
    }
  };

  const fetchComments = async () => {
    try {
      const response: any = await axios.get(`/vendors/vendor_invoices/${id}/comments`);
      setComments(response.data || []);
    } catch (error: any) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const fetchActivityLog = async () => {
    try {
      const response: any = await axios.get(`/vendors/vendor_invoices/${id}/activity_log`);
      setActivityLog(response.data?.daily_summary || []);
    } catch (error: any) {
      console.error('Failed to fetch activity log:', error);
    }
  };

  const handleSubmitNote = async () => {
    if (!newNote.trim()) return;
    setSubmittingNote(true);
    try {
      await axios.post(`/vendors/vendor_invoices/${id}/comments`, {
        comment: { body: newNote.trim() },
      });
      setNewNote('');
      fetchComments();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to add note';
      showErrorSnackBar(errorMessage);
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleDeleteNote = async (commentId: number) => {
    try {
      await axios.delete(`/vendors/vendor_invoices/${id}/comments/${commentId}`);
      showSuccessSnackBar('Note deleted');
      fetchComments();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to delete note';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleNoteKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitNote();
    }
  };

  useEffect(() => {
    fetchInvoice();
    fetchVendors();
    fetchComments();
    fetchActivityLog();
  }, [id]);

  // Set presigned PDF URL when invoice loads
  useEffect(() => {
    if (invoice?.pdf_url) {
      setPdfUrl(invoice.pdf_url);
    }
  }, [invoice?.pdf_url]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box>
        <Typography variant="h6">Invoice not found</Typography>
      </Box>
    );
  }

  // Spreadsheet (xlsx) statements can't be embedded like PDFs — show a download card instead.
  const isSpreadsheetStatement = /spreadsheet|excel|\.xlsx?$/i.test(
    invoice.statement_content_type || invoice.pdf_filename || '',
  );

  return (
    <Box style={{ width: '100%' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 20 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.goBack()}>
          Back to List
        </Button>
        {invoice.pdf_url && (
          <Button
            variant="outlined"
            startIcon={isSpreadsheetStatement ? <DownloadIcon /> : <PictureAsPdfIcon />}
            href={invoice.pdf_url}
            target="_blank"
            size="small"
          >
            {isSpreadsheetStatement ? 'Download Spreadsheet' : 'Open PDF'}
          </Button>
        )}
      </Box>

      {invoice.location && (
        <Box
          sx={{
            mb: 2,
            py: 1,
            px: 2,
            borderRadius: 1,
            backgroundColor: invoice.location.toLowerCase() === 'tampa' ? '#e65100' : '#1565c0',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {invoice.location.charAt(0).toUpperCase() + invoice.location.slice(1).toLowerCase()}
          </Typography>
        </Box>
      )}

      {invoice.extraction_error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>Statement extraction failed:</strong> {invoice.extraction_error}
        </Alert>
      )}

      {/* Invoice Summary + PDF Embed */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {/* Left Side: Vendor Info & Invoice Details */}
        <Grid item xs={12} md={invoice.pdf_url ? 5 : 12}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography variant="h5" component="h1" gutterBottom>
                    {invoice.resolved_vendor_name}
                  </Typography>
                </Box>
                {!editing && (
                  <Button variant="contained" color="primary" onClick={() => setEditing(true)} size="small">
                    Edit
                  </Button>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {editing ? (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Autocomplete
                        options={vendors}
                        getOptionLabel={(option) => `${option.vendor_name} (${option.vendor_id})`}
                        value={selectedVendor}
                        onChange={handleVendorChange}
                        loading={loadingVendors}
                        onInputChange={(_, newInputValue) => {
                          if (newInputValue.length > 2) {
                            fetchVendors(newInputValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Vendor Lookup"
                            size="small"
                            helperText="Search for vendor by name"
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.vendor_id === value.vendor_id}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                  <Box display="flex" gap={2} mt={2}>
                    <Button variant="contained" color="primary" onClick={handleUpdate} size="small">
                      Save Changes
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setEditing(false);
                        setFormData({
                          vendor_id: invoice.vendor_id || '',
                          location: invoice.location || '',
                          notes: invoice.notes || '',
                        });
                        setSelectedVendor(invoice.vendor_details || null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Received At
                      </Typography>
                      <Typography variant="body1">
                        {invoice.received_at || invoice.created_at
                          ? new Date(invoice.received_at || invoice.created_at).toLocaleString('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : 'N/A'}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Total Amount
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {invoice.line_items && invoice.line_items.length > 0
                          ? `$${invoice.line_items
                              .reduce((sum: number, li: any) => sum + (parseFloat(li.amount) || 0), 0)
                              .toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                          : '$0.00'}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Line Items
                      </Typography>
                      <Typography variant="body1">
                        {invoice.line_items && invoice.line_items.length > 0 ? (
                          <Chip label={`${invoice.line_items.length} items`} size="small" color="default" />
                        ) : (
                          'None'
                        )}
                      </Typography>
                    </Grid>

                    {invoice.source_type === 'outlook_email' && invoice.source_id && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Source Email
                        </Typography>
                        <Button
                          variant="text"
                          size="small"
                          sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                          onClick={() => history.push(`/vendors/outlook-emails/${invoice.source_id}`)}
                        >
                          View Outlook Email →
                        </Button>
                      </Grid>
                    )}

                    {invoice.notes && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Notes
                        </Typography>
                        <Typography variant="body2">{invoice.notes}</Typography>
                      </Grid>
                    )}
                  </Grid>

                  {invoice.vendor_details && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Vendor Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Vendor Name
                          </Typography>
                          <Typography variant="body2">{invoice.vendor_details.vendor_name}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Vendor ID
                          </Typography>
                          <Typography variant="body2">{invoice.vendor_details.vendor_id}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Phone
                          </Typography>
                          <Typography variant="body2">{invoice.vendor_details.phone || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Email
                          </Typography>
                          <Typography variant="body2">{invoice.vendor_details.email || 'N/A'}</Typography>
                        </Grid>
                        {(invoice.vendor_details.address1 || invoice.vendor_details.city) && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Address
                            </Typography>
                            <Typography variant="body2">
                              {[
                                invoice.vendor_details.address1,
                                invoice.vendor_details.address2,
                                [invoice.vendor_details.city, invoice.vendor_details.state, invoice.vendor_details.zip]
                                  .filter(Boolean)
                                  .join(', '),
                              ]
                                .filter(Boolean)
                                .join(', ') || 'N/A'}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side: Embedded PDF */}
        {invoice.pdf_url && (
          <Grid item xs={12} md={7}>
            <Card sx={{ height: '100%', minHeight: 700 }}>
              <CardContent sx={{ height: '100%', p: 0, '&:last-child': { pb: 0 } }}>
                {isSpreadsheetStatement ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    minHeight={700}
                    gap={2}
                    p={3}
                  >
                    <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                    <Typography variant="subtitle1" align="center">
                      {invoice.pdf_filename || 'Spreadsheet statement'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      Spreadsheet statements can't be previewed inline. Line items were extracted below.
                    </Typography>
                    <Button variant="contained" startIcon={<DownloadIcon />} href={invoice.pdf_url} target="_blank">
                      Download Spreadsheet
                    </Button>
                  </Box>
                ) : pdfUrl ? (
                  <iframe
                    src={pdfUrl}
                    title={invoice.pdf_filename || 'Invoice PDF'}
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: 700,
                      border: 'none',
                    }}
                  />
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={700}>
                    <CircularProgress />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Ignore Line Item Dialog */}
      <Dialog open={ignoreDialog.lineItemId != null} onClose={closeIgnoreDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Ignore line item</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A reason is required so we can understand later why this line was ignored. This note will show in the
            activity log.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            value={ignoreDialog.reason}
            onChange={(e) => setIgnoreDialog((prev) => ({ ...prev, reason: e.target.value }))}
            disabled={ignoreDialog.submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeIgnoreDialog} disabled={ignoreDialog.submitting}>
            Cancel
          </Button>
          <Button
            onClick={submitIgnoreDialog}
            variant="contained"
            color="primary"
            disabled={ignoreDialog.submitting || !ignoreDialog.reason.trim()}
          >
            {ignoreDialog.submitting ? 'Ignoring…' : 'Ignore'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extracted Text Modal */}
      <Dialog open={extractedTextModalOpen} onClose={() => setExtractedTextModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6">Extracted Text</Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant={viewMode === 'text' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setViewMode('text')}
                >
                  View as Text
                </Button>
                <Button
                  variant={viewMode === 'json' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setViewMode('json')}
                  disabled={!invoice.textract_response}
                >
                  View as JSON
                </Button>
              </Box>
            </Box>
            <IconButton edge="end" onClick={() => setExtractedTextModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            value={
              viewMode === 'text'
                ? invoice.raw_extracted_text || 'No extracted text available'
                : invoice.textract_response
                  ? JSON.stringify(invoice.textract_response, null, 2)
                  : 'No JSON data available'
            }
            fullWidth
            multiline
            rows={20}
            variant="outlined"
            InputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtractedTextModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Match Detail Modal */}
      <Dialog
        open={matchDetailModal.open}
        onClose={() => setMatchDetailModal({ open: false, type: null, data: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {matchDetailModal.type === 'servman' ? 'ServMan Invoice Details' : 'DocuWare Invoice Details'}
            </Typography>
            <IconButton
              edge="end"
              onClick={() => setMatchDetailModal({ open: false, type: null, data: null })}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {matchDetailModal.data ? (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Location
                </Typography>
                <Typography variant="body1">{matchDetailModal.data.location || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Vendor ID
                </Typography>
                <Typography variant="body1">{matchDetailModal.data.vendor_id || 'N/A'}</Typography>
              </Grid>
              {matchDetailModal.type === 'servman' && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Vendor Name
                  </Typography>
                  <Typography variant="body1">{matchDetailModal.data.vendor_name || 'N/A'}</Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Invoice Number
                </Typography>
                <Typography variant="body1">{matchDetailModal.data.invoice_number || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Invoice Date
                </Typography>
                <Typography variant="body1">
                  {matchDetailModal.data.invoice_date
                    ? new Date(matchDetailModal.data.invoice_date).toLocaleDateString()
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Invoice Amount
                </Typography>
                <Typography variant="body1">
                  {matchDetailModal.data.invoice_amount != null
                    ? `$${parseFloat(matchDetailModal.data.invoice_amount.toString()).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`
                    : 'N/A'}
                </Typography>
              </Grid>
              {matchDetailModal.type === 'servman' && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Transaction Type
                  </Typography>
                  <Typography variant="body1">{matchDetailModal.data.trantype || 'N/A'}</Typography>
                </Grid>
              )}
              {matchDetailModal.type === 'docuware' && matchDetailModal.data.docid && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    DocuWare Doc ID
                  </Typography>
                  <Typography variant="body1">{matchDetailModal.data.docid}</Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Last Synced
                </Typography>
                <Typography variant="body1">
                  {matchDetailModal.data.synced_at ? new Date(matchDetailModal.data.synced_at).toLocaleString() : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography>No details available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchDetailModal({ open: false, type: null, data: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Validations Section */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5" component="h2" fontWeight="600" gutterBottom>
                Invoice Validations
              </Typography>
              {invoice.validated_at && (
                <Typography variant="caption" color="text.secondary">
                  Last validated: {new Date(invoice.validated_at).toLocaleString()}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Summary */}
          {invoice.line_items &&
            invoice.line_items.length > 0 &&
            (() => {
              const total = invoice.line_items!.length;
              const isServmanOk = (li: any) =>
                li.servman_invoice_id && !li.servman_amount_mismatch && !li.servman_vendor_mismatch;
              const isDocuwareOk = (li: any) =>
                li.servman_docuware_invoice_id && !li.docuware_amount_mismatch && !li.docuware_vendor_mismatch;
              const servmanMatched = invoice.line_items!.filter(isServmanOk).length;
              const docuwareMatched = invoice.line_items!.filter(isDocuwareOk).length;
              const fullyMatched = invoice.line_items!.filter((li: any) => isServmanOk(li) && isDocuwareOk(li)).length;
              const unmatched = invoice.line_items!.filter((li: any) => !isServmanOk(li) || !isDocuwareOk(li)).length;
              const totalAmount = invoice.line_items!.reduce(
                (sum: number, li: any) => sum + (parseFloat(li.amount) || 0),
                0,
              );
              return (
                <Box mb={3}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={2}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold">
                          {total}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total Items
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold" color="success.main">
                          {fullyMatched}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Fully Matched
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold" color="error.main">
                          {unmatched}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          With Issues
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold">
                          {servmanMatched}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ServMan
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold">
                          {docuwareMatched}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          DocuWare
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold">
                          ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total Amount
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              );
            })()}

          {/* Category Filter Chips + Line Items Table */}
          {invoice.line_items && invoice.line_items.length > 0 ? (
            (() => {
              const classifyLineItem = (li: any): string => {
                if (li.ignored_at) return 'ignored';
                if (li.servman_amount_mismatch || li.docuware_amount_mismatch) return 'amount_discrepancy';
                if (li.servman_vendor_mismatch || li.docuware_vendor_mismatch) return 'vendor_discrepancy';
                const servmanOk = li.servman_invoice_id && !li.servman_amount_mismatch && !li.servman_vendor_mismatch;
                const docuwareOk =
                  li.servman_docuware_invoice_id && !li.docuware_amount_mismatch && !li.docuware_vendor_mismatch;
                if (servmanOk && docuwareOk) return 'matched';
                return 'missing';
              };

              const categoryCounts: Record<string, number> = {
                matched: 0,
                missing: 0,
                amount_discrepancy: 0,
                vendor_discrepancy: 0,
                ignored: 0,
              };
              invoice.line_items!.forEach((li: any) => {
                const cat = classifyLineItem(li);
                categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
              });

              const categoryConfig: Array<{ key: string; label: string; color: any }> = [
                { key: 'matched', label: 'Matched', color: 'success' },
                { key: 'missing', label: 'Missing', color: 'error' },
                { key: 'amount_discrepancy', label: 'Amount Discrepancy', color: 'warning' },
                { key: 'vendor_discrepancy', label: 'Vendor Discrepancy', color: 'info' },
                { key: 'ignored', label: 'Ignored', color: 'secondary' },
              ];

              const toggleCategory = (key: string) => {
                setActiveCategories((prev) => (prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]));
              };

              const filteredItems = invoice
                .line_items!.filter((li: any) => activeCategories.includes(classifyLineItem(li)))
                .sort((a: any, b: any) => {
                  const numA = a.invoice_number ? String(a.invoice_number) : '';
                  const numB = b.invoice_number ? String(b.invoice_number) : '';
                  return numA.localeCompare(numB, undefined, { numeric: true });
                });

              return (
                <>
                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    {categoryConfig.map((cat) => (
                      <Chip
                        key={cat.key}
                        label={`${cat.label} (${categoryCounts[cat.key] || 0})`}
                        color={activeCategories.includes(cat.key) ? cat.color : 'default'}
                        variant={activeCategories.includes(cat.key) ? 'filled' : 'outlined'}
                        icon={
                          activeCategories.includes(cat.key) ? (
                            <VisibilityIcon fontSize="small" />
                          ) : (
                            <VisibilityOffIcon fontSize="small" />
                          )
                        }
                        onClick={() => toggleCategory(cat.key)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      {selectedLineItemIds.length > 0
                        ? `${selectedLineItemIds.length} line item${selectedLineItemIds.length === 1 ? '' : 's'} selected`
                        : 'Select line items to send a reminder.'}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<SendIcon />}
                      disabled={selectedLineItemIds.length === 0}
                      onClick={() => setSendReminderOpen(true)}
                    >
                      Send Reminders
                    </Button>
                  </Box>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              indeterminate={
                                selectedLineItemIds.length > 0 &&
                                selectedLineItemIds.length < filteredItems.filter((li: any) => !li.ignored_at).length
                              }
                              checked={
                                filteredItems.filter((li: any) => !li.ignored_at).length > 0 &&
                                selectedLineItemIds.length === filteredItems.filter((li: any) => !li.ignored_at).length
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedLineItemIds(
                                    filteredItems.filter((li: any) => !li.ignored_at).map((li: any) => li.id),
                                  );
                                } else {
                                  setSelectedLineItemIds([]);
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <strong>Invoice Number</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Amount</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Exists in ServMan</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Exists in DocuWare</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>DocuWare Status</strong>
                          </TableCell>
                          <TableCell align="center" width={80}>
                            <strong>Actions</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredItems.map((lineItem: any, index: number) => (
                          <TableRow
                            key={index}
                            sx={lineItem.ignored_at ? { opacity: 0.5, bgcolor: 'grey.50' } : undefined}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                disabled={!!lineItem.ignored_at}
                                checked={selectedLineItemIds.includes(lineItem.id)}
                                onChange={() => toggleLineItemSelection(lineItem.id)}
                              />
                            </TableCell>
                            <TableCell>{lineItem.invoice_number || 'N/A'}</TableCell>
                            <TableCell>
                              <EditableAmountCell
                                lineItem={lineItem}
                                vendorInvoiceId={id}
                                disabled={!!lineItem.ignored_at}
                                onSaved={fetchInvoice}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {lineItem.ignored_at ? (
                                <Typography variant="body2" color="text.disabled">
                                  Ignored
                                </Typography>
                              ) : lineItem.servman_invoice_id &&
                                !lineItem.servman_amount_mismatch &&
                                !lineItem.servman_vendor_mismatch ? (
                                <Tooltip
                                  title={
                                    lineItem.servman_sibling_match
                                      ? 'Matched via sibling location (e.g. Trane PGH/Tampa)'
                                      : ''
                                  }
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={1}
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() =>
                                      setMatchDetailModal({
                                        open: true,
                                        type: 'servman',
                                        data: lineItem.servman_invoice,
                                      })
                                    }
                                  >
                                    <CheckCircleIcon fontSize="small" color="success" />
                                    <Typography
                                      variant="body2"
                                      color="success.main"
                                      sx={{ textDecoration: 'underline' }}
                                    >
                                      {lineItem.servman_sibling_match ? 'Yes (Other Loc)' : 'Yes'}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              ) : lineItem.servman_amount_mismatch ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  gap={1}
                                  sx={{ cursor: 'pointer' }}
                                  onClick={() =>
                                    setMatchDetailModal({
                                      open: true,
                                      type: 'servman',
                                      data: lineItem.servman_invoice,
                                    })
                                  }
                                >
                                  <WarningIcon fontSize="small" color="warning" />
                                  <Typography variant="body2" color="warning.main" sx={{ textDecoration: 'underline' }}>
                                    Amount Mismatch
                                  </Typography>
                                </Box>
                              ) : lineItem.servman_vendor_mismatch ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  gap={1}
                                  sx={{ cursor: 'pointer' }}
                                  onClick={() =>
                                    setMatchDetailModal({
                                      open: true,
                                      type: 'servman',
                                      data: lineItem.servman_invoice,
                                    })
                                  }
                                >
                                  <InfoIcon fontSize="small" color="info" />
                                  <Typography variant="body2" color="info.main" sx={{ textDecoration: 'underline' }}>
                                    Wrong Vendor
                                  </Typography>
                                </Box>
                              ) : (
                                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                  <ErrorIcon fontSize="small" color="error" />
                                  <Typography variant="body2" color="error.main">
                                    No
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {lineItem.ignored_at ? (
                                <Typography variant="body2" color="text.disabled">
                                  Ignored
                                </Typography>
                              ) : lineItem.servman_docuware_invoice_id &&
                                !lineItem.docuware_amount_mismatch &&
                                !lineItem.docuware_vendor_mismatch ? (
                                <Tooltip
                                  title={
                                    lineItem.docuware_sibling_match
                                      ? 'Matched via sibling location (e.g. Trane PGH/Tampa)'
                                      : ''
                                  }
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={1}
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() =>
                                      setMatchDetailModal({
                                        open: true,
                                        type: 'docuware',
                                        data: lineItem.servman_docuware_invoice,
                                      })
                                    }
                                  >
                                    <CheckCircleIcon fontSize="small" color="success" />
                                    <Typography
                                      variant="body2"
                                      color="success.main"
                                      sx={{ textDecoration: 'underline' }}
                                    >
                                      {lineItem.docuware_sibling_match ? 'Yes (Other Loc)' : 'Yes'}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              ) : lineItem.docuware_amount_mismatch ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  gap={1}
                                  sx={{ cursor: 'pointer' }}
                                  onClick={() =>
                                    setMatchDetailModal({
                                      open: true,
                                      type: 'docuware',
                                      data: lineItem.servman_docuware_invoice,
                                    })
                                  }
                                >
                                  <WarningIcon fontSize="small" color="warning" />
                                  <Typography variant="body2" color="warning.main" sx={{ textDecoration: 'underline' }}>
                                    Amount Mismatch
                                  </Typography>
                                </Box>
                              ) : lineItem.docuware_vendor_mismatch ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  gap={1}
                                  sx={{ cursor: 'pointer' }}
                                  onClick={() =>
                                    setMatchDetailModal({
                                      open: true,
                                      type: 'docuware',
                                      data: lineItem.servman_docuware_invoice,
                                    })
                                  }
                                >
                                  <InfoIcon fontSize="small" color="info" />
                                  <Typography variant="body2" color="info.main" sx={{ textDecoration: 'underline' }}>
                                    Wrong Vendor
                                  </Typography>
                                </Box>
                              ) : (
                                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                  <ErrorIcon fontSize="small" color="error" />
                                  <Typography variant="body2" color="error.main">
                                    No
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {(() => {
                                const dw = lineItem.servman_docuware_invoice;
                                const status = dw?.docuware_status;
                                const exportStatus = dw?.export_status;
                                if (!status && !exportStatus) {
                                  return (
                                    <Typography variant="body2" color="text.disabled">
                                      —
                                    </Typography>
                                  );
                                }
                                const label =
                                  status && exportStatus
                                    ? `${status} (${exportStatus})`
                                    : status || `(${exportStatus})`;
                                return <Typography variant="body2">{label}</Typography>;
                              })()}
                            </TableCell>
                            <TableCell align="center">
                              {lineItem.ignored_at ? (
                                <Tooltip title="Unignore this line item">
                                  <IconButton size="small" onClick={() => handleUnignoreLineItem(lineItem.id)}>
                                    <UndoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : classifyLineItem(lineItem) !== 'matched' ? (
                                <Tooltip title="Ignore this discrepancy">
                                  <IconButton size="small" onClick={() => openIgnoreDialog(lineItem.id)}>
                                    <DoNotDisturbIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        ))}
                        {filteredItems.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                No line items match the selected categories.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              );
            })()
          ) : (
            <Alert severity="info">No line items available for validation</Alert>
          )}

          {/* Action Buttons */}
          <Box mt={3}>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="flex-start" gap={2}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleRevalidate}
                disabled={revalidating}
                startIcon={revalidating ? <CircularProgress size={20} /> : undefined}
              >
                {revalidating ? 'Re-validating...' : 'Re-validate Invoice'}
              </Button>
              {invoice.validation_status !== 'ignored' && (
                <Button variant="outlined" color="inherit" onClick={handleMarkAsIgnored} startIcon={<BlockIcon />}>
                  Ignore Document / Statement
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Notes & Activity Log Section */}
      <Grid container spacing={3} style={{ marginTop: 8 }}>
        {/* Notes */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <ChatBubbleOutlineIcon color={invoice.flagged_for_review ? 'error' : 'action'} />
                  <Typography variant="h6" color={invoice.flagged_for_review ? 'error' : 'inherit'}>
                    Notes
                  </Typography>
                  {comments.length > 0 && (
                    <Chip
                      label={comments.length}
                      size="small"
                      color={invoice.flagged_for_review ? 'error' : 'default'}
                    />
                  )}
                </Box>
                <Tooltip
                  title={invoice.flagged_for_review ? 'Unflag this invoice' : 'Flag as programming/system issue'}
                >
                  <Button
                    size="small"
                    variant={invoice.flagged_for_review ? 'contained' : 'outlined'}
                    color="error"
                    onClick={handleToggleFlag}
                    startIcon={invoice.flagged_for_review ? <FlagIcon /> : <OutlinedFlagIcon />}
                  >
                    {invoice.flagged_for_review ? 'Flagged' : 'Flag for Review'}
                  </Button>
                </Tooltip>
              </Box>

              {comments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No notes yet. Add one below.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 400, overflowY: 'auto', mb: 2 }}>
                  {comments.map((comment, index) => (
                    <Box key={comment.id}>
                      <Box py={1.5}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                              <Typography variant="subtitle2">{comment.user?.full_name || 'Unknown User'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(comment.created_at).toLocaleString('en-US', {
                                  month: '2-digit',
                                  day: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </Typography>
                            </Box>
                            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                              {comment.body}
                            </Typography>
                          </Box>
                          <Tooltip title="Delete note">
                            <IconButton size="small" onClick={() => handleDeleteNote(comment.id)} sx={{ ml: 1 }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      {index < comments.length - 1 && <Divider />}
                    </Box>
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 1 }} />
              <Box display="flex" gap={1} mt={1}>
                <TextField
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  onKeyDown={handleNoteKeyDown}
                  placeholder="Add a note about this invoice..."
                  fullWidth
                  multiline
                  maxRows={3}
                  size="small"
                  disabled={submittingNote}
                />
                <Button
                  onClick={handleSubmitNote}
                  variant="contained"
                  disabled={!newNote.trim() || submittingNote}
                  startIcon={submittingNote ? <CircularProgress size={16} /> : <SendIcon />}
                  sx={{ minWidth: 90, alignSelf: 'flex-end' }}
                >
                  Add
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Activity Log */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TimelineIcon color="action" />
                  <Typography variant="h6">Activity Log</Typography>
                </Box>
                {invoice.validated_at && (
                  <Typography variant="caption" color="text.secondary">
                    Last validated:{' '}
                    {new Date(invoice.validated_at).toLocaleString('en-US', {
                      month: '2-digit',
                      day: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                )}
              </Box>

              {activityLog.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No resolution activity recorded yet.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Date</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>ServMan</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>DocuWare</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Ignored</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activityLog.map((entry) => (
                        <TableRow key={entry.date}>
                          <TableCell>{entry.date}</TableCell>
                          <TableCell align="center">
                            {entry.servman_resolved > 0 ? (
                              <Chip
                                label={`${entry.servman_resolved}/${entry.total}`}
                                color={entry.servman_resolved >= entry.total ? 'success' : 'warning'}
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                0/{entry.total}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {entry.docuware_resolved > 0 ? (
                              <Chip
                                label={`${entry.docuware_resolved}/${entry.total}`}
                                color={entry.docuware_resolved >= entry.total ? 'success' : 'warning'}
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                0/{entry.total}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {entry.ignored_count > 0 ? (
                              <Chip
                                label={`${entry.ignored_count}/${entry.total}`}
                                color="default"
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                0/{entry.total}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <SendReminderModal
        open={sendReminderOpen}
        vendorInvoiceId={id}
        selectedLineItemIds={selectedLineItemIds}
        onClose={() => setSendReminderOpen(false)}
        onSent={() => setSelectedLineItemIds([])}
      />
    </Box>
  );
};

export default VendorInvoiceDetailPage;
